import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle, Clock, Users, Search, ArrowUpDown, ArrowUp, ArrowDown, Download, Send, AlertTriangle, Ban, RotateCcw } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { logAdminAction, logUpdate } from '@/lib/audit-log';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DataPagination } from '@/components/DataPagination';

const CAP_JOIN_REWARD = 30;
const DEFAULT_PAGE_SIZE = 10;

// The CAP upload is the source of truth (20260925000000_cap_sheet_driven_activation.sql):
// match_cap_acceptances() activates every pending/invite_sent/expired
// application in the sheet and reactivates auto_dropout deactivations;
// deactivate_cap_dropouts() deactivates active creators missing from it.
// Rejected and manually-deactivated applications are never touched by an
// upload — those stay admin decisions.
//
// 'expired' (invite_sent 7+ days with no acceptance) is now display-only:
// it no longer blocks activation. status and is_expired are always written
// together.
type Status = 'pending' | 'invite_sent' | 'expired' | 'active' | 'deactivated' | 'rejected';
type TabValue = 'all' | Status;

// Creator-facing "why you're not eligible" presets for the reject dialog. The
// chosen text is saved to cap_applications.rejection_reason and sent to the
// creator (in-app + push + email) by notify_cap_application_status_change();
// the admin can edit it before rejecting. `notes` stays admin-internal.
// The preset's `code` goes to cap_applications.rejection_reason_code (CHECK
// constraint lists the same values); 'another_agency' makes the notification,
// email and app status screen add the "how to unlink" next steps.
type RejectionReasonCode = 'another_agency' | 'no_ecommerce_permission' | 'username_not_found' | 'not_us_tiktok_shop';
const INELIGIBLE_REASONS: { code: RejectionReasonCode; label: string; text: string }[] = [
  { code: 'another_agency',          label: 'Another agency',          text: 'Your TikTok account is already connected to another TikTok Shop agency.' },
  { code: 'no_ecommerce_permission', label: 'No ecommerce permission', text: "Your TikTok account doesn't have ecommerce permission enabled yet." },
  { code: 'username_not_found',      label: 'Username not found',      text: "We couldn't find your TikTok username. Please check the TikTok handle on your profile and re-apply." },
  { code: 'not_us_tiktok_shop',      label: 'Not US TikTok Shop',      text: "Your TikTok Shop account isn't US-based. Our CAP is only open to US TikTok Shop creators." },
];
const MIN_REJECTION_REASON_LENGTH = 10;

// Manual activation requires an invite first: pending → invite_sent → active.
const MANUALLY_ACTIVATABLE: Status[] = ['invite_sent', 'expired'];

type Application = {
  id: string;
  user_id: string;
  status: Status;
  applied_at: string;
  activated_at: string | null;
  activated_via: 'report_match' | 'manual' | null;
  invite_sent_at: string | null;
  invited_by: string | null;
  reinvited_at: string | null;
  reinvite_count: number;
  is_expired: boolean;
  expired_at: string | null;
  expired_count: number;
  rejected_at: string | null;
  rejection_reason: string | null;
  rejection_reason_code: RejectionReasonCode | null;
  notes: string | null;
  retry_count: number;
  last_reapplied_at: string | null;
  deactivated_at: string | null;
  deactivated_reason: string | null;
  deactivated_by: string | null;
  deactivated_via: 'manual' | 'auto_dropout' | null;
  deactivated_count: number;
  reactivated_at: string | null;
  reactivated_by: string | null;
  /** Where the latest (re-)application was requested (20261029000001). */
  source: 'creator_app' | 'coach_portal';
  /** Handle the Coach portal applied with; the matcher's fallback when the profile has none. */
  tiktok_handle: string | null;
  profiles: {
    id: string;
    full_name: string | null;
    tiktok_handle: string | null;
    email: string | null;
    avatar_url: string | null;
  } | null;
};

const SOURCE_LABEL: Record<Application['source'], string> = { creator_app: 'Creator app', coach_portal: 'Coach portal' };

/** The handle CAP matching uses: the profile's, else the one applied with from the Coach portal. */
const capHandle = (app: Application | null | undefined) => app?.profiles?.tiktok_handle || app?.tiktok_handle || null;

type SortCol = 'name' | 'handle' | 'applied' | 'status' | 'retries' | 'last_reapplied';
type SortDir = 'asc' | 'desc';

// Maps UI sort columns to DB columns and which table they live on
const SORT_MAP: Record<SortCol, { column: string; referencedTable?: string }> = {
  name:    { column: 'full_name',      referencedTable: 'profiles' },
  handle:  { column: 'tiktok_handle',  referencedTable: 'profiles' },
  applied: { column: 'applied_at' },
  status:  { column: 'status' },
  retries:       { column: 'retry_count' },
  last_reapplied: { column: 'last_reapplied_at' },
};

async function getAdminEmail(): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.user?.email || 'unknown';
}

function StatusBadge({ app }: { app: Application }) {
  if (app.status === 'active')      return <Badge className="bg-green-100 text-green-800 border-green-200 border">Active</Badge>;
  if (app.status === 'deactivated') return <Badge className="bg-gray-200 text-gray-800 border-gray-300 border">Deactivated</Badge>;
  if (app.status === 'rejected') return <Badge className="bg-red-100 text-red-800 border-red-200 border">Rejected</Badge>;
  if (app.status === 'expired')  return <Badge className="bg-orange-100 text-orange-800 border-orange-200 border">Expired</Badge>;
  if (app.status === 'invite_sent') return <Badge className="bg-blue-100 text-blue-800 border-blue-200 border">Invite Sent</Badge>;
  return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 border">Pending</Badge>;
}

function ActivatedViaBadge({ activatedVia }: { activatedVia: Application['activated_via'] }) {
  if (activatedVia === 'report_match') return <Badge variant="outline" className="text-xs">Report match</Badge>;
  if (activatedVia === 'manual') return <Badge variant="outline" className="text-xs">Manual override</Badge>;
  return <span className="text-muted-foreground">—</span>;
}

function DeactivatedViaBadge({ deactivatedVia }: { deactivatedVia: Application['deactivated_via'] }) {
  if (deactivatedVia === 'auto_dropout') return <Badge variant="outline" className="text-xs">Dropped from upload</Badge>;
  if (deactivatedVia === 'manual') return <Badge variant="outline" className="text-xs">Manual</Badge>;
  return <span className="text-muted-foreground">—</span>;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function SortIcon({ col, sortCol, sortDir }: { col: SortCol; sortCol: SortCol; sortDir: SortDir }) {
  if (sortCol !== col) return <ArrowUpDown className="ml-1 h-3.5 w-3.5 text-muted-foreground/60 inline" />;
  return sortDir === 'asc'
    ? <ArrowUp className="ml-1 h-3.5 w-3.5 inline" />
    : <ArrowDown className="ml-1 h-3.5 w-3.5 inline" />;
}

export default function CAPApplications() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab]     = useState<TabValue>('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch]           = useState('');        // debounced
  const [sortCol, setSortCol]         = useState<SortCol>('applied');
  const [sortDir, setSortDir]         = useState<SortDir>('desc');
  const [page, setPage]               = useState(1);
  const [pageSize, setPageSize]       = useState(DEFAULT_PAGE_SIZE);
  const [isExporting, setIsExporting]             = useState(false);
  const [inviteSentTarget, setInviteSentTarget]   = useState<Application | null>(null);
  const [reinviteTarget, setReinviteTarget]       = useState<Application | null>(null);
  const [activateTarget, setActivateTarget]       = useState<Application | null>(null);
  const [rejectTarget, setRejectTarget]           = useState<Application | null>(null);
  const [rejectNotes, setRejectNotes]             = useState('');
  const [rejectReason, setRejectReason]           = useState('');
  const [rejectReasonCode, setRejectReasonCode]   = useState<RejectionReasonCode | null>(null);
  const [deactivateTarget, setDeactivateTarget]   = useState<Application | null>(null);
  const [deactivateReason, setDeactivateReason]   = useState('');
  const [reactivateTarget, setReactivateTarget]   = useState<Application | null>(null);

  // Debounce search input by 300 ms
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Main paginated query — all filtering/sorting/pagination happens in the DB
  const { data, isLoading } = useQuery({
    queryKey: ['cap-applications', activeTab, search, sortCol, sortDir, page, pageSize],
    queryFn: async () => {
      // Use !inner when searching so PostgREST does an INNER JOIN — this excludes
      // cap_applications rows where the profile doesn't match, instead of just
      // nullifying the profiles embed while still returning the parent row.
      const profilesJoin = search.trim()
        ? 'profiles!inner(id, full_name, tiktok_handle, email, avatar_url)'
        : 'profiles(id, full_name, tiktok_handle, email, avatar_url)';

      let query = supabase
        .from('cap_applications')
        .select(`*, ${profilesJoin}`, { count: 'exact' });

      if (activeTab !== 'all') {
        query = query.eq('status', activeTab);
      }

      if (search.trim()) {
        query = query.or(
          `full_name.ilike.%${search}%,email.ilike.%${search}%,tiktok_handle.ilike.%${search}%`,
          { referencedTable: 'profiles' },
        );
      }

      const { column, referencedTable } = SORT_MAP[sortCol];
      query = query.order(column, { ascending: sortDir === 'asc', referencedTable } as any);

      const from = (page - 1) * pageSize;
      query = query.range(from, from + pageSize - 1);

      const { data: rows, error, count } = await query;
      if (error) throw error;
      return { rows: (rows ?? []) as Application[], total: count ?? 0 };
    },
  });

  // Tab badge counts (unfettered by current filters). Each is a head-only,
  // count='exact' request — no rows are returned, so these are true table-wide
  // counts regardless of PostgREST's default row cap. A plain `.select('status')`
  // (the previous approach) silently truncated at that cap, so `all` and every
  // per-status count under-reported once cap_applications passed ~1,000 rows.
  const STATUS_LIST: Status[] = ['pending', 'invite_sent', 'expired', 'active', 'deactivated', 'rejected'];
  const { data: counts = { all: 0, pending: 0, invite_sent: 0, expired: 0, active: 0, deactivated: 0, rejected: 0 } } = useQuery({
    queryKey: ['cap-applications-counts'],
    queryFn: async () => {
      const [totalRes, ...statusRes] = await Promise.all([
        supabase.from('cap_applications').select('*', { count: 'exact', head: true }),
        ...STATUS_LIST.map(status =>
          supabase.from('cap_applications').select('*', { count: 'exact', head: true }).eq('status', status)
        ),
      ]);
      if (totalRes.error) throw totalRes.error;
      const result = { all: totalRes.count ?? 0 } as Record<'all' | Status, number>;
      STATUS_LIST.forEach((status, i) => {
        if (statusRes[i].error) throw statusRes[i].error;
        result[status] = statusRes[i].count ?? 0;
      });
      return result;
    },
    staleTime: 30_000,
  });

  const rows      = data?.rows  ?? [];
  const total     = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  function handleSort(col: SortCol) {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
    setPage(1);
  }

  function handleTabChange(v: string) {
    setActiveTab(v as any);
    setSearchInput('');
    setSearch('');
    setPage(1);
  }

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['cap-applications'] });
    queryClient.invalidateQueries({ queryKey: ['cap-applications-counts'] });
    queryClient.invalidateQueries({ queryKey: ['cap-applications-pending-count'] });
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const BATCH = 1000;
      let allRows: Application[] = [];
      let batch = 0;

      while (true) {
        const profilesJoin = search.trim()
          ? 'profiles!inner(id, full_name, tiktok_handle, email, avatar_url)'
          : 'profiles(id, full_name, tiktok_handle, email, avatar_url)';

        let q = supabase
          .from('cap_applications')
          .select(`*, ${profilesJoin}`);

        if (activeTab !== 'all') {
          q = q.eq('status', activeTab);
        }

        if (search.trim()) {
          q = q.or(
            `full_name.ilike.%${search}%,email.ilike.%${search}%,tiktok_handle.ilike.%${search}%`,
            { referencedTable: 'profiles' },
          );
        }

        const { column, referencedTable } = SORT_MAP[sortCol];
        q = q.order(column, { ascending: sortDir === 'asc', referencedTable } as any);

        const { data: pageData, error } = await q.range(batch * BATCH, (batch + 1) * BATCH - 1);
        if (error) throw error;
        if (pageData && pageData.length > 0) allRows = allRows.concat(pageData as Application[]);
        if (!pageData || pageData.length < BATCH) break;
        batch++;
      }

      if (allRows.length === 0) {
        toast.error('No data to export');
        return;
      }

      const escapeCsv = (v: any) => {
        const s = v == null ? '' : String(v);
        return `"${s.replace(/"/g, '""')}"`;
      };

      const headers = [
        'Name', 'Email', 'TikTok Handle', 'Status', 'Source', 'Applied Date', 'Invite Sent Date',
        'Reinvited Date', 'Reinvite Count', 'Expired', 'Last Expired Date', 'Expired Count',
        'Activated Date', 'Activated Via', 'Deactivated Date', 'Deactivated Via', 'Deactivated Reason',
        'Deactivated By', 'Deactivated Count', 'Reactivated Date', 'Reactivated By',
        'Rejected Date', 'Rejection Reason (creator)', 'Notes',
      ];
      const csvRows = allRows.map(r => [
        r.profiles?.full_name ?? '',
        r.profiles?.email ?? '',
        capHandle(r) ? `@${capHandle(r)}` : '',
        r.status,
        SOURCE_LABEL[r.source] ?? r.source ?? '',
        r.applied_at ? new Date(r.applied_at).toLocaleDateString('en-US') : '',
        r.invite_sent_at ? new Date(r.invite_sent_at).toLocaleDateString('en-US') : '',
        r.reinvited_at ? new Date(r.reinvited_at).toLocaleDateString('en-US') : '',
        r.reinvite_count > 0 ? r.reinvite_count : '',
        r.is_expired ? 'Yes' : '',
        r.expired_at ? new Date(r.expired_at).toLocaleDateString('en-US') : '',
        r.expired_count > 0 ? r.expired_count : '',
        r.activated_at ? new Date(r.activated_at).toLocaleDateString('en-US') : '',
        r.activated_via ?? '',
        r.deactivated_at ? new Date(r.deactivated_at).toLocaleDateString('en-US') : '',
        r.deactivated_via ?? '',
        r.deactivated_reason ?? '',
        r.deactivated_by ?? '',
        r.deactivated_count > 0 ? r.deactivated_count : '',
        r.reactivated_at ? new Date(r.reactivated_at).toLocaleDateString('en-US') : '',
        r.reactivated_by ?? '',
        r.rejected_at ? new Date(r.rejected_at).toLocaleDateString('en-US') : '',
        r.rejection_reason ?? '',
        r.notes ?? '',
      ].map(escapeCsv).join(','));

      const csv = [headers.join(','), ...csvRows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cap-applications-${activeTab}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${allRows.length} applications`);
    } catch (err: any) {
      toast.error(err.message || 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  // ── Mark invite sent (pending → invite_sent) ────────────────────────────────
  const markInviteSentMutation = useMutation({
    mutationFn: async (app: Application) => {
      const now = new Date().toISOString();
      const adminEmail = await getAdminEmail();
      try {
        const { data: current, error: fetchError } = await supabase
          .from('cap_applications')
          .select('status')
          .eq('id', app.id)
          .single();
        if (fetchError) throw fetchError;
        if (current?.status !== 'pending') {
          throw new Error('Only pending applications can be marked as invite sent.');
        }

        const { error } = await supabase
          .from('cap_applications')
          .update({ status: 'invite_sent', invite_sent_at: now, invited_by: adminEmail })
          .eq('id', app.id);
        if (error) throw error;

        await logUpdate('cap_applications', app.id,
          { status: app.status },
          { status: 'invite_sent', invite_sent_at: now, invited_by: adminEmail },
          { description: 'CAP TikTok invite marked as sent' }
        );
      } catch (err: any) {
        await logUpdate('cap_applications', app.id,
          { status: app.status },
          { status: 'invite_sent' },
          { description: 'CAP mark-invite-sent failed', status: 'failed', error: err.message }
        );
        throw err;
      }
    },
    onSuccess: () => {
      toast.success('Invite marked as sent — creator notified');
      invalidateAll();
      setInviteSentTarget(null);
    },
    onError: (err: any) => toast.error(err.message),
  });

  // ── Re-invite (invite_sent | expired → invite_sent, timer reset, reinvite_count++) ─
  const reinviteMutation = useMutation({
    mutationFn: async (app: Application) => {
      const now = new Date().toISOString();
      const adminEmail = await getAdminEmail();
      try {
        const { data: current, error: fetchError } = await supabase
          .from('cap_applications')
          .select('status, reinvite_count')
          .eq('id', app.id)
          .single();
        if (fetchError) throw fetchError;
        if (current?.status !== 'invite_sent' && current?.status !== 'expired') {
          throw new Error('Only applications with an invite already sent (or expired) can be re-invited.');
        }
        const nextReinviteCount = (current.reinvite_count ?? 0) + 1;

        // Sending a fresh invite starts a new 7-day window — flips status
        // AND is_expired back together (from expired, if that's where it
        // was) so match_cap_acceptances() can reach it again. expired_at/
        // expired_count are left as-is (lifetime history, never reset).
        const { error } = await supabase
          .from('cap_applications')
          .update({
            status: 'invite_sent', is_expired: false, invite_sent_at: now, reinvited_at: now,
            invited_by: adminEmail, reinvite_count: nextReinviteCount,
          })
          .eq('id', app.id);
        if (error) throw error;

        await logUpdate('cap_applications', app.id,
          { status: current.status, invite_sent_at: app.invite_sent_at, reinvite_count: app.reinvite_count },
          { status: 'invite_sent', is_expired: false, invite_sent_at: now, reinvited_at: now, invited_by: adminEmail, reinvite_count: nextReinviteCount },
          { description: 'CAP TikTok invite re-sent' }
        );
      } catch (err: any) {
        await logUpdate('cap_applications', app.id,
          { invite_sent_at: app.invite_sent_at },
          { invite_sent_at: now },
          { description: 'CAP re-invite failed', status: 'failed', error: err.message }
        );
        throw err;
      }
    },
    onSuccess: () => {
      toast.success('Re-invite recorded — creator notified');
      invalidateAll();
      setReinviteTarget(null);
    },
    onError: (err: any) => toast.error(err.message),
  });

  // ── Manual activation override (invite_sent | expired → active) ────────────
  // For activating without waiting for the next CAP upload. Note the next
  // upload still deactivates them if they're absent from it.
  const manualActivateMutation = useMutation({
    mutationFn: async (app: Application) => {
      const now = new Date().toISOString();
      try {
        const { data: current, error: fetchError } = await supabase
          .from('cap_applications')
          .select('status')
          .eq('id', app.id)
          .single();
        if (fetchError) throw fetchError;
        if (!MANUALLY_ACTIVATABLE.includes(current?.status)) {
          throw new Error('Send the invite first — only invite sent or expired applications can be manually activated.');
        }

        const { error: statusError } = await supabase
          .from('cap_applications')
          .update({ status: 'active', is_expired: false, activated_at: now, activated_via: 'manual' })
          .eq('id', app.id);
        if (statusError) throw statusError;

        const { error: profileError } = await supabase
          .from('profiles')
          .update({ is_cap_member: true, cap_joined_at: now })
          .eq('id', app.user_id);
        if (profileError) throw profileError;

        // COIN SYSTEM DISABLED — CAP join reward no longer granted
        // const { error: gemsError } = await supabase.rpc('admin_add_gems', {
        //   p_user_id: app.user_id,
        //   p_amount: CAP_JOIN_REWARD,
        // });
        // if (gemsError) throw gemsError;
        //
        // await supabase.from('credit_transactions').insert({
        //   user_id: app.user_id,
        //   amount: CAP_JOIN_REWARD,
        //   type: 'cap_join',
        //   description: 'CAP program activation reward (manual override)',
        //   created_at: now,
        // });

        await logUpdate('cap_applications', app.id,
          { status: current.status },
          { status: 'active', activated_at: now, activated_via: 'manual' },
          // { description: `CAP application manually activated — ${CAP_JOIN_REWARD} gems awarded` }
          { description: 'CAP application manually activated' }
        );
      } catch (err: any) {
        await logUpdate('cap_applications', app.id,
          { status: app.status },
          { status: 'active' },
          { description: 'CAP manual activation failed', status: 'failed', error: err.message }
        );
        throw err;
      }
    },
    onSuccess: () => {
      // COIN SYSTEM DISABLED: toast.success(`Application activated and ${CAP_JOIN_REWARD} gems awarded`);
      toast.success('Application activated');
      invalidateAll();
      setActivateTarget(null);
    },
    onError: (err: any) => toast.error(err.message),
  });

  // ── Deactivate (active → deactivated) ───────────────────────────────────────
  // No gem clawback — deactivation isn't a punishment for the join reward,
  // it's just turning CAP feature access off. profiles.is_cap_member is what
  // every CAP-gated surface (Hidden Gems, flash sale alerts, My Showcase)
  // actually reads, so it has to flip together with the status, in the same
  // way manualActivateMutation flips it on. The guard triggers added in
  // 20260918000000_cap_deactivation.sql stop the creator's own
  // cap_applications_update_own / profiles_update_own UPDATE from undoing
  // this — admin_all lets this admin-authenticated write through.
  const deactivateMutation = useMutation({
    mutationFn: async ({ app, reason }: { app: Application; reason: string }) => {
      const now = new Date().toISOString();
      const adminEmail = await getAdminEmail();
      try {
        const { data: current, error: fetchError } = await supabase
          .from('cap_applications')
          .select('status, deactivated_count')
          .eq('id', app.id)
          .single();
        if (fetchError) throw fetchError;
        if (current?.status !== 'active') {
          throw new Error('Only active applications can be deactivated.');
        }

        const { error: statusError } = await supabase
          .from('cap_applications')
          .update({
            status: 'deactivated', deactivated_at: now, deactivated_via: 'manual',
            deactivated_reason: reason || null, deactivated_by: adminEmail,
            deactivated_count: (current.deactivated_count ?? 0) + 1,
          })
          .eq('id', app.id);
        if (statusError) throw statusError;

        const { error: profileError } = await supabase
          .from('profiles')
          .update({ is_cap_member: false })
          .eq('id', app.user_id);
        if (profileError) throw profileError;

        await logUpdate('cap_applications', app.id,
          { status: current.status },
          { status: 'deactivated', deactivated_at: now, deactivated_reason: reason },
          { description: 'CAP membership deactivated' }
        );
      } catch (err: any) {
        await logUpdate('cap_applications', app.id,
          { status: app.status },
          { status: 'deactivated' },
          { description: 'CAP deactivation failed', status: 'failed', error: err.message }
        );
        throw err;
      }
    },
    onSuccess: () => {
      toast.success('CAP membership deactivated');
      invalidateAll();
      setDeactivateTarget(null);
      setDeactivateReason('');
    },
    onError: (err: any) => toast.error(err.message),
  });

  // ── Reactivate (deactivated → active) ───────────────────────────────────────
  // Auto-dropouts also come back on their own when they reappear in an
  // upload; manual deactivations only come back through this. No gem
  // re-award: the 30-gem CAP join reward is one-time per applicant.
  const reactivateMutation = useMutation({
    mutationFn: async (app: Application) => {
      const now = new Date().toISOString();
      const adminEmail = await getAdminEmail();
      try {
        const { data: current, error: fetchError } = await supabase
          .from('cap_applications')
          .select('status')
          .eq('id', app.id)
          .single();
        if (fetchError) throw fetchError;
        if (current?.status !== 'deactivated') {
          throw new Error('Only deactivated applications can be reactivated.');
        }

        const { error: statusError } = await supabase
          .from('cap_applications')
          .update({ status: 'active', reactivated_at: now, reactivated_by: adminEmail })
          .eq('id', app.id);
        if (statusError) throw statusError;

        const { error: profileError } = await supabase
          .from('profiles')
          .update({ is_cap_member: true })
          .eq('id', app.user_id);
        if (profileError) throw profileError;

        await logUpdate('cap_applications', app.id,
          { status: current.status },
          { status: 'active', reactivated_at: now },
          { description: 'CAP membership reactivated' }
        );
      } catch (err: any) {
        await logUpdate('cap_applications', app.id,
          { status: app.status },
          { status: 'active' },
          { description: 'CAP reactivation failed', status: 'failed', error: err.message }
        );
        throw err;
      }
    },
    onSuccess: () => {
      toast.success('CAP membership reactivated');
      invalidateAll();
      setReactivateTarget(null);
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Rejection is only allowed while still pending — once the TikTok invite
  // has been sent, the paths forward are Re-invite, upload-match activation,
  // or the manual activation override.
  const rejectMutation = useMutation({
    mutationFn: async ({ app, notes, reason, reasonCode }: { app: Application; notes: string; reason: string; reasonCode: RejectionReasonCode | null }) => {
      const now = new Date().toISOString();
      try {
        const { data: current, error: fetchError } = await supabase
          .from('cap_applications')
          .select('status')
          .eq('id', app.id)
          .single();
        if (fetchError) throw fetchError;
        if (current?.status !== 'pending') {
          throw new Error('Only pending applications can be rejected — an invite has already been sent for this one.');
        }

        const { error } = await supabase
          .from('cap_applications')
          .update({ status: 'rejected', rejected_at: now, rejection_reason: reason, rejection_reason_code: reasonCode, notes: notes || null })
          .eq('id', app.id);
        if (error) throw error;

        await logUpdate('cap_applications', app.id,
          { status: app.status },
          { status: 'rejected', rejected_at: now, rejection_reason: reason, rejection_reason_code: reasonCode, notes },
          { description: 'CAP application rejected' }
        );
      } catch (err: any) {
        await logUpdate('cap_applications', app.id,
          { status: app.status },
          { status: 'rejected' },
          { description: 'CAP rejection failed', status: 'failed', error: err.message }
        );
        throw err;
      }
    },
    onSuccess: () => {
      toast.success('Application rejected — creator notified');
      invalidateAll();
      setRejectTarget(null);
      setRejectNotes('');
      setRejectReason('');
      setRejectReasonCode(null);
    },
    onError: (err: any) => toast.error(err.message),
  });

  // ── Manual matcher run (backfill/testing) — defaults to the latest upload ──
  const runMatcherMutation = useMutation({
    mutationFn: async () => {
      try {
        const { data, error } = await supabase.rpc('match_cap_acceptances', {});
        if (error) throw error;
        const matchedCount = (data as number) ?? 0;
        await logAdminAction({
          action: 'UPDATE',
          tableName: 'cap_applications',
          metadata: { description: `Manually ran CAP acceptance matcher — ${matchedCount} activated`, matched_count: matchedCount, trigger: 'manual' },
        });
        return matchedCount;
      } catch (err: any) {
        await logAdminAction({
          action: 'UPDATE',
          tableName: 'cap_applications',
          metadata: { description: 'Manual CAP acceptance matcher run failed', status: 'failed', error: err.message, trigger: 'manual' },
        });
        throw err;
      }
    },
    onSuccess: (matchedCount) => {
      toast.success(matchedCount > 0 ? `${matchedCount} application(s) auto-activated` : 'No new matches found');
      invalidateAll();
    },
    onError: (err: any) => toast.error(err.message),
  });

  const tabs = ['all', 'pending', 'invite_sent', 'expired', 'active', 'deactivated', 'rejected'] as const;
  const tabLabels: Record<typeof tabs[number], string> = {
    all: 'All', pending: 'Pending', invite_sent: 'Invite Sent', expired: 'Expired', active: 'Active',
    deactivated: 'Deactivated', rejected: 'Rejected',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">CAP Applications</h1>
          <p className="text-muted-foreground text-sm">Creator Agency Partner program applications</p>
        </div>
        {/* <Button
          variant="outline"
          size="sm"
          onClick={() => runMatcherMutation.mutate()}
          disabled={runMatcherMutation.isPending}
        >
          <Send className="mr-1.5 h-4 w-4" />
          {runMatcherMutation.isPending ? 'Running...' : 'Run Matcher Now'}
        </Button> */}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Users className="h-4 w-4" /> Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{counts.all}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Clock className="h-4 w-4" /> Pending
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-yellow-600">{counts.pending}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Send className="h-4 w-4" /> Invite Sent
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-blue-600">{counts.invite_sent}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" /> Expired
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-orange-600">{counts.expired}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle className="h-4 w-4" /> Active
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-green-600">{counts.active}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Ban className="h-4 w-4" /> Deactivated
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-600">{counts.deactivated}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              Rejected
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-red-600">{counts.rejected}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-4">
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            <div className="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
              <TabsList>
                <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
                <TabsTrigger value="pending">
                  Pending {counts.pending > 0 && (
                    <span className="ml-1.5 rounded-full bg-yellow-500 text-white text-[10px] font-bold px-1.5 py-0.5">
                      {counts.pending}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="invite_sent">
                  Invite Sent {counts.invite_sent > 0 && (
                    <span className="ml-1.5 rounded-full bg-blue-500 text-white text-[10px] font-bold px-1.5 py-0.5">
                      {counts.invite_sent}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="expired">
                  Expired {counts.expired > 0 && (
                    <span className="ml-1.5 rounded-full bg-orange-500 text-white text-[10px] font-bold px-1.5 py-0.5">
                      {counts.expired}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="active">Active ({counts.active})</TabsTrigger>
                <TabsTrigger value="deactivated">Deactivated ({counts.deactivated})</TabsTrigger>
                <TabsTrigger value="rejected">Rejected ({counts.rejected})</TabsTrigger>
              </TabsList>

              <div className="flex flex-wrap items-center gap-2">
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search name, email, handle..."
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="pl-8 h-8 text-sm"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  disabled={isExporting || total === 0}
                >
                  <Download className="mr-1.5 h-4 w-4" />
                  {isExporting ? 'Exporting...' : 'Export'}
                </Button>
              </div>
            </div>

            {tabs.map((tab) => (
              <TabsContent key={tab} value={tab}>
                <div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead
                          className="cursor-pointer select-none hover:text-foreground whitespace-nowrap"
                          onClick={() => handleSort('name')}
                        >
                          Creator <SortIcon col="name" sortCol={sortCol} sortDir={sortDir} />
                        </TableHead>
                        <TableHead
                          className="cursor-pointer select-none hover:text-foreground whitespace-nowrap"
                          onClick={() => handleSort('handle')}
                        >
                          TikTok Handle <SortIcon col="handle" sortCol={sortCol} sortDir={sortDir} />
                        </TableHead>
                        <TableHead
                          className="cursor-pointer select-none hover:text-foreground whitespace-nowrap"
                          onClick={() => handleSort('applied')}
                        >
                          Applied <SortIcon col="applied" sortCol={sortCol} sortDir={sortDir} />
                        </TableHead>
                        <TableHead
                          className="cursor-pointer select-none hover:text-foreground whitespace-nowrap"
                          onClick={() => handleSort('status')}
                        >
                          Status <SortIcon col="status" sortCol={sortCol} sortDir={sortDir} />
                        </TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead className="whitespace-nowrap">Invite Sent</TableHead>
                        <TableHead className="whitespace-nowrap">Reinvited</TableHead>
                        <TableHead className="whitespace-nowrap">Expired</TableHead>
                        <TableHead className="whitespace-nowrap">Activated</TableHead>
                        <TableHead>Activated Via</TableHead>
                        <TableHead className="whitespace-nowrap">Deactivated</TableHead>
                        <TableHead>Deactivated Via</TableHead>
                        {tab === 'deactivated' && <TableHead>Reason</TableHead>}
                        <TableHead className="whitespace-nowrap">Rejected</TableHead>
                        {tab === 'rejected' && <TableHead>Reason (creator)</TableHead>}
                        {tab === 'rejected' && <TableHead>Notes</TableHead>}
                        <TableHead
                          className="cursor-pointer select-none hover:text-foreground whitespace-nowrap"
                          onClick={() => handleSort('retries')}
                        >
                          Retries <SortIcon col="retries" sortCol={sortCol} sortDir={sortDir} />
                        </TableHead>
                        <TableHead
                          className="cursor-pointer select-none hover:text-foreground whitespace-nowrap"
                          onClick={() => handleSort('last_reapplied')}
                        >
                          Last Re-applied <SortIcon col="last_reapplied" sortCol={sortCol} sortDir={sortDir} />
                        </TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isLoading ? (
                        <TableRow>
                          <TableCell colSpan={tab === 'rejected' ? 18 : tab === 'deactivated' ? 17 : 16} className="h-24 text-center text-muted-foreground">
                            Loading...
                          </TableCell>
                        </TableRow>
                      ) : rows?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={tab === 'rejected' ? 18 : tab === 'deactivated' ? 17 : 16} className="h-24 text-center text-muted-foreground">
                            {search ? 'No results match your search' : `No ${tabLabels[tab].toLowerCase()} applications`}
                          </TableCell>
                        </TableRow>
                      ) : (
                        rows?.map((app) => {
                          return (
                          <TableRow key={app.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{app.profiles?.full_name || '—'}</p>
                                <p className="text-xs text-muted-foreground">{app.profiles?.email || '—'}</p>
                              </div>
                            </TableCell>
                            <TableCell className="whitespace-nowrap text-sm">
                              {capHandle(app) ? `@${capHandle(app)}` : '—'}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {formatDate(app.applied_at)}
                            </TableCell>
                            <TableCell>
                              <StatusBadge app={app} />
                            </TableCell>
                            <TableCell>
                              <Badge variant={app.source === 'coach_portal' ? 'default' : 'outline'} className="whitespace-nowrap">
                                {SOURCE_LABEL[app.source] ?? 'Creator app'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                              {app.invite_sent_at ? formatDate(app.invite_sent_at) : '—'}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                              {app.reinvited_at ? (
                                <>
                                  {formatDate(app.reinvited_at)}
                                  {app.reinvite_count > 0 && <span className="text-xs"> (×{app.reinvite_count})</span>}
                                </>
                              ) : '—'}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                              {app.expired_at ? (
                                <span className={app.is_expired ? 'text-orange-600 font-medium' : ''}>
                                  {formatDate(app.expired_at)}
                                  {app.expired_count > 0 && <span className="text-xs"> (×{app.expired_count})</span>}
                                </span>
                              ) : '—'}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                              {app.activated_at ? formatDate(app.activated_at) : '—'}
                            </TableCell>
                            <TableCell>
                              <ActivatedViaBadge activatedVia={app.activated_via} />
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                              {app.deactivated_at ? (
                                <>
                                  {formatDate(app.deactivated_at)}
                                  {app.deactivated_count > 1 && <span className="text-xs"> (×{app.deactivated_count})</span>}
                                  {app.reactivated_at && (
                                    <div className="text-xs text-green-600">Reactivated {formatDate(app.reactivated_at)}</div>
                                  )}
                                </>
                              ) : '—'}
                            </TableCell>
                            <TableCell>
                              <DeactivatedViaBadge deactivatedVia={app.deactivated_via} />
                            </TableCell>
                            {tab === 'deactivated' && (
                              <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                                {app.deactivated_reason || '—'}
                              </TableCell>
                            )}
                            <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                              {app.rejected_at ? formatDate(app.rejected_at) : '—'}
                            </TableCell>
                            {tab === 'rejected' && (
                              <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate" title={app.rejection_reason ?? undefined}>
                                {app.rejection_reason || '—'}
                              </TableCell>
                            )}
                            {tab === 'rejected' && (
                              <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                                {app.notes || '—'}
                              </TableCell>
                            )}
                            <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                              {app.retry_count > 0 ? app.retry_count : '—'}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                              {app.last_reapplied_at ? formatDate(app.last_reapplied_at) : '—'}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-2">
                                {app.status === 'pending' && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="default"
                                      onClick={() => setInviteSentTarget(app)}
                                    >
                                      Mark Invite Sent
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-red-600 border-red-200 hover:bg-red-50"
                                      onClick={() => { setRejectTarget(app); setRejectNotes(''); }}
                                    >
                                      Reject
                                    </Button>
                                  </>
                                )}
                                {MANUALLY_ACTIVATABLE.includes(app.status) && (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => setReinviteTarget(app)}
                                    >
                                      Re-invite
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="default"
                                      className="bg-green-600 hover:bg-green-700"
                                      onClick={() => setActivateTarget(app)}
                                    >
                                      Mark Active
                                    </Button>
                                  </>
                                )}
                                {app.status === 'active' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-gray-700 border-gray-300 hover:bg-gray-100"
                                    onClick={() => { setDeactivateTarget(app); setDeactivateReason(''); }}
                                  >
                                    <Ban className="mr-1.5 h-3.5 w-3.5" />
                                    Deactivate
                                  </Button>
                                )}
                                {app.status === 'deactivated' && (
                                  <Button
                                    size="sm"
                                    variant="default"
                                    className="bg-green-600 hover:bg-green-700"
                                    onClick={() => setReactivateTarget(app)}
                                  >
                                    <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                                    Reactivate
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {total > 0 && (
                  <DataPagination
                    currentPage={page}
                    totalPages={totalPages}
                    pageSize={pageSize}
                    totalItems={total}
                    onPageChange={setPage}
                    onPageSizeChange={(size) => {
                      setPageSize(size);
                      setPage(1);
                    }}
                    className="mt-4"
                  />
                )}
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Mark invite sent confirmation */}
      <AlertDialog open={!!inviteSentTarget} onOpenChange={(o) => { if (!o && !markInviteSentMutation.isPending) setInviteSentTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark TikTok Invite as Sent</AlertDialogTitle>
            <AlertDialogDescription>
              Confirm you've sent the CAP TikTok agency invite to{' '}
              <strong>{inviteSentTarget?.profiles?.full_name || inviteSentTarget?.profiles?.email}</strong>
              {capHandle(inviteSentTarget) && <> (@{capHandle(inviteSentTarget)})</>}.
              They'll be auto-activated the next time they appear in a CAP data upload.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              disabled={markInviteSentMutation.isPending}
              onClick={() => setInviteSentTarget(null)}
            >
              Cancel
            </Button>
            <Button
              disabled={markInviteSentMutation.isPending}
              onClick={() => inviteSentTarget && markInviteSentMutation.mutate(inviteSentTarget)}
            >
              {markInviteSentMutation.isPending ? 'Saving...' : 'Mark Invite Sent'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Re-invite confirmation */}
      <AlertDialog open={!!reinviteTarget} onOpenChange={(o) => { if (!o && !reinviteMutation.isPending) setReinviteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-send TikTok Invite</AlertDialogTitle>
            <AlertDialogDescription>
              Confirm you've re-sent the CAP TikTok agency invite to{' '}
              <strong>{reinviteTarget?.profiles?.full_name || reinviteTarget?.profiles?.email}</strong>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              disabled={reinviteMutation.isPending}
              onClick={() => setReinviteTarget(null)}
            >
              Cancel
            </Button>
            <Button
              disabled={reinviteMutation.isPending}
              onClick={() => reinviteTarget && reinviteMutation.mutate(reinviteTarget)}
            >
              {reinviteMutation.isPending ? 'Saving...' : 'Re-invite'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manual activation override confirmation */}
      <AlertDialog open={!!activateTarget} onOpenChange={(o) => { if (!o && !manualActivateMutation.isPending) setActivateTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Manually Activate CAP Membership</AlertDialogTitle>
            <AlertDialogDescription>
              Activate <strong>{activateTarget?.profiles?.full_name || activateTarget?.profiles?.email}</strong> without
              waiting for a CAP data report match — use this for creators who accepted the invite but will never show
              up in a report (e.g. $0 sales). They will be marked as a CAP member.{/* COIN SYSTEM DISABLED: and receive <strong>{CAP_JOIN_REWARD} gems</strong> */}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              disabled={manualActivateMutation.isPending}
              onClick={() => setActivateTarget(null)}
            >
              Cancel
            </Button>
            <Button
              disabled={manualActivateMutation.isPending}
              onClick={() => activateTarget && manualActivateMutation.mutate(activateTarget)}
              className="bg-green-600 hover:bg-green-700"
            >
              {/* COIN SYSTEM DISABLED: `Activate & Award ${CAP_JOIN_REWARD} Gems` */}
              {manualActivateMutation.isPending ? 'Activating...' : 'Activate'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Deactivate dialog (active only) */}
      <Dialog open={!!deactivateTarget} onOpenChange={(o) => { if (!o && !deactivateMutation.isPending) { setDeactivateTarget(null); setDeactivateReason(''); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Deactivate CAP Membership</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Deactivate <strong>{deactivateTarget?.profiles?.full_name || deactivateTarget?.profiles?.email}</strong>?
              They will immediately lose access to CAP features (Hidden Gems full access, flash sale alerts, My Showcase)
              until an admin reactivates them. This does not claw back gems already awarded.
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="deactivate-reason">Reason (optional)</Label>
              <Textarea
                id="deactivate-reason"
                placeholder="Reason for deactivation..."
                value={deactivateReason}
                onChange={(e) => setDeactivateReason(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={deactivateMutation.isPending}
              onClick={() => { setDeactivateTarget(null); setDeactivateReason(''); }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deactivateMutation.isPending}
              onClick={() => deactivateTarget && deactivateMutation.mutate({ app: deactivateTarget, reason: deactivateReason })}
            >
              {deactivateMutation.isPending ? 'Deactivating...' : 'Deactivate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reactivate confirmation (deactivated only) */}
      <AlertDialog open={!!reactivateTarget} onOpenChange={(o) => { if (!o && !reactivateMutation.isPending) setReactivateTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reactivate CAP Membership</AlertDialogTitle>
            <AlertDialogDescription>
              Restore CAP feature access for{' '}
              <strong>{reactivateTarget?.profiles?.full_name || reactivateTarget?.profiles?.email}</strong>.
              No gems are re-awarded — this only restores access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              disabled={reactivateMutation.isPending}
              onClick={() => setReactivateTarget(null)}
            >
              Cancel
            </Button>
            <Button
              disabled={reactivateMutation.isPending}
              onClick={() => reactivateTarget && reactivateMutation.mutate(reactivateTarget)}
              className="bg-green-600 hover:bg-green-700"
            >
              {reactivateMutation.isPending ? 'Reactivating...' : 'Reactivate'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject dialog (pending only) */}
      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o && !rejectMutation.isPending) { setRejectTarget(null); setRejectNotes(''); setRejectReason(''); setRejectReasonCode(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject CAP Application</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Reject the application from <strong>{rejectTarget?.profiles?.full_name || rejectTarget?.profiles?.email}</strong>?
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="reject-reason">Why they're not eligible (shown to the creator)</Label>
              <div className="flex flex-wrap gap-1.5">
                {INELIGIBLE_REASONS.map((r) => (
                  <Button
                    key={r.code}
                    type="button"
                    size="sm"
                    variant={rejectReasonCode === r.code ? 'default' : 'outline'}
                    className="h-7 text-xs"
                    onClick={() => {
                      // Clicking the selected preset again clears it (custom reason).
                      if (rejectReasonCode === r.code) {
                        setRejectReasonCode(null);
                        setRejectReason('');
                      } else {
                        setRejectReasonCode(r.code);
                        setRejectReason(r.text);
                      }
                    }}
                  >
                    {r.label}
                  </Button>
                ))}
              </div>
              <Textarea
                id="reject-reason"
                placeholder="Pick a reason above or write your own..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                The creator gets this in an in-app notification, a push and an email, and sees it on their CAP status screen.
              </p>
              {rejectReasonCode === 'another_agency' && (
                <p className="text-xs text-muted-foreground">
                  The creator also gets the steps to unlink from their current agency (TikTok Shop → Settings → Partnerships) and re-apply.
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reject-notes">Internal notes (optional, admin only)</Label>
              <Textarea
                id="reject-notes"
                placeholder="Notes for the team — not shown to the creator..."
                value={rejectNotes}
                onChange={(e) => setRejectNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={rejectMutation.isPending}
              onClick={() => { setRejectTarget(null); setRejectNotes(''); setRejectReason(''); setRejectReasonCode(null); }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={rejectMutation.isPending || rejectReason.trim().length < MIN_REJECTION_REASON_LENGTH}
              onClick={() => rejectTarget && rejectMutation.mutate({ app: rejectTarget, notes: rejectNotes.trim(), reason: rejectReason.trim(), reasonCode: rejectReasonCode })}
            >
              {rejectMutation.isPending ? 'Rejecting...' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
